import { Check, Circle, Clock, Play, Printer, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import type { ItemCounterConfig } from '@/lib/item-counter'
import type { KdsScreenSettings, TillColor } from '@/lib/kds-settings'
import { type KitchenFlowIndex, kitchenRows } from '@/lib/kitchen-components'
import { canPrepare, type ItemAction, nextItemAction, remainingItems } from '@/lib/preparation'
import { ageSeconds, type DisplayMode, items, type Slip, slipState, visibleSections } from '@/lib/slips'
import { cn } from '@/lib/utils'

/**
 * How long an order has waited. Past an hour it gains an hours field: an order left over
 * from yesterday should read as a time, not as a four-digit number of minutes.
 */
function elapsed(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  const pad = (value: number) => String(value).padStart(2, '0')
  if (minutes < 60) return `${minutes}:${pad(seconds % 60)}`
  return `${Math.floor(minutes / 60)}:${pad(minutes % 60)}:${pad(seconds % 60)}`
}

/**
 * Native item preparation, with locally retained copies for closed orders.
 */
export function SlipCard({
  slip,
  flowIndex,
  stationNames,
  mode,
  settings,
  scale,
  now,
  lateColor,
  tillColor,
  showsPrint,
  showsComplete,
  finishLabel,
  kept,
  recalled = false,
  onClearKept,
  busy,
  onFinishSlip,
  onPickedUp,
  onPrint,
  displayId,
  skipStart,
  counterConfig,
  onItemAction,
  onAccept,
  onDecline,
}: {
  slip: Slip
  flowIndex?: KitchenFlowIndex
  stationNames: ReadonlyMap<string, string>
  mode: Exclude<DisplayMode, 'auto'>
  settings: KdsScreenSettings
  scale: number
  now: number
  lateColor: string | null
  tillColor: TillColor | null
  showsPrint: boolean
  showsComplete: boolean
  finishLabel: string
  /** Held on the rail after the API stopped returning it, because it was paid for early. */
  kept: boolean
  recalled?: boolean
  onClearKept: () => void
  busy: boolean
  onFinishSlip: () => void
  onPickedUp: () => void
  onPrint: () => void
  displayId: string
  skipStart: boolean
  counterConfig: ItemCounterConfig
  onItemAction: (itemId: string, action: ItemAction) => void
  onAccept: () => void
  onDecline: () => void
}) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const state = slipState(slip, mode)
  const sections = visibleSections(slip, mode)
  const age = ageSeconds(slip, now)
  const remaining = remainingItems(slip, displayId, mode, counterConfig)
  const progress = (slip.kitchenStationProgress ?? []).filter((station) => station.state !== 'NotRequired')

  return (
    <article
      className={cn('flex min-w-0 flex-col overflow-hidden rounded-xl border-2 bg-card shadow-sm', state === 'ready' && 'opacity-60')}
      style={{
        borderColor: kept ? '#f59e0b' : (lateColor ?? undefined),
        borderLeftWidth: tillColor ? 10 : undefined,
        borderLeftColor: tillColor?.color,
        fontSize: `${scale}rem`,
      }}
    >
      <header className="flex items-center justify-between gap-2 px-3 py-2" style={{ backgroundColor: lateColor ?? slip.backgroundColor ?? undefined }}>
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[1.4em] font-bold tabular-nums">{slip.humanOrderCode ?? '—'}</span>
          {tillColor?.label && (
            <span className="rounded px-1.5 py-0.5 text-[0.7em] font-bold text-white" style={{ backgroundColor: tillColor.color }}>
              {tillColor.label}
            </span>
          )}
          {kept && <span className="shrink-0 rounded bg-amber-500 px-1.5 py-0.5 text-[0.7em] font-bold text-black">{recalled ? 'ÅTERSTÄLLD KOPIA' : 'BETALD'}</span>}
          {slip.orderType && <span className="truncate rounded bg-foreground/10 px-1.5 py-0.5 text-[0.7em] font-semibold uppercase">{slip.orderType}</span>}
        </div>
        <span className="flex shrink-0 items-center gap-1 text-[0.85em] font-semibold tabular-nums">
          <Clock className="h-[1em] w-[1em]" />
          {elapsed(age)}
        </span>
      </header>

      {slip.needsConfirmation && !kept && (
        <div className="flex gap-2 border-b p-3">
          <Button disabled={busy} onClick={onAccept}>
            Acceptera
          </Button>
          <Button variant="destructive" disabled={busy} onClick={onDecline}>
            Avvisa
          </Button>
        </div>
      )}
      {(slip.tableName || slip.message) && (
        <div className="border-b bg-muted/40 px-3 py-1.5 text-[0.85em]">
          {slip.tableName && <span className="font-semibold">{slip.tableName}</span>}
          {slip.tableName && slip.message && ' · '}
          {slip.message}
        </div>
      )}

      {progress.length > 0 && (
        <ul aria-label="Stationsstatus" className="flex flex-wrap gap-2 border-b px-3 py-2 text-[0.75em]">
          {progress.map((station) => (
            <li key={station.kitchenDisplayId} className={cn('flex items-center gap-1.5 rounded-lg border px-2 py-1', station.state === 'Done' && 'bg-primary/10 font-semibold')}>
              {station.state === 'Done' ? <Check aria-hidden="true" className="h-[1em] w-[1em]" /> : <Circle aria-hidden="true" className="h-[1em] w-[1em]" />}
              <span>
                {stationNames.get(station.kitchenDisplayId) ?? 'Köksstation'}: {station.state === 'Done' ? 'Klar' : 'Väntar'}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className={cn('min-h-0 flex-1 divide-y', !settings.slipFullHeight && 'overflow-y-auto')} style={{ maxHeight: settings.slipFullHeight ? undefined : settings.slipMaxHeight }}>
        {sections.map((section) => {
          return (
            <div key={section.id} style={{ opacity: section.isSecondary && !settings.treatAllAsPrimary ? settings.secondaryOpacity / 100 : undefined }}>
              {section.title &&
                (section.isCollapsed ? (
                  <button
                    type="button"
                    className="w-full bg-muted/40 px-3 py-2 text-left text-[0.75em] font-semibold"
                    onClick={() =>
                      setExpanded((previous) => {
                        const next = new Set(previous)
                        if (next.has(section.id)) next.delete(section.id)
                        else next.add(section.id)
                        return next
                      })
                    }
                    aria-expanded={expanded.has(section.id)}
                  >
                    {section.title} · {expanded.has(section.id) ? 'Dölj' : 'Visa'} {items(section).length} artiklar
                  </button>
                ) : (
                  <p className="bg-muted/40 px-3 py-1 text-[0.75em] font-semibold">{section.title}</p>
                ))}
              <ul hidden={Boolean(section.isCollapsed && !expanded.has(section.id))}>
                {items(section).map((item) => {
                  const rows = kitchenRows(item, flowIndex?.get(item.productId ?? ''))
                  const automatic = displayId === 'main' && counterConfig.enabled && counterConfig.autoCompleteProductNames.includes(item.name ?? '')
                  const own = canPrepare(section, item, displayId) && !slip.readyForPickup && !slip.needsConfirmation && !automatic
                  const completed = Boolean(item.preparationCompletedAt) || automatic
                  const action = nextItemAction(item, skipStart)
                  return (
                    <li key={item.id}>
                      <div className={cn('flex w-full items-start gap-2 px-3 py-2 text-left', completed && 'bg-primary/10', item.preparationStartedAt && !completed && 'bg-testmode/10')}>
                        {own ? (
                          <Button
                            variant={completed ? 'secondary' : 'outline'}
                            size="icon"
                            disabled={busy}
                            aria-label={`${completed ? 'Återställ' : action === 'start' ? 'Påbörja' : 'Markera klar'} ${item.name ?? 'artikel'}`}
                            onClick={() => onItemAction(item.id, completed ? 'clear' : action)}
                          >
                            {completed ? <RotateCcw /> : action === 'start' ? <Play /> : <Check />}
                          </Button>
                        ) : completed ? (
                          <Check className="mt-2 size-5 shrink-0 text-primary" aria-label="Tillagad" />
                        ) : (
                          <Circle className="mt-2 size-5 shrink-0 text-muted-foreground" />
                        )}
                        <span
                          className="min-w-0 flex-1"
                          style={{
                            fontWeight: settings.bold ? 700 : undefined,
                            fontStyle: settings.italic ? 'italic' : undefined,
                          }}
                        >
                          {rows.some((row) => row.id !== item.id) && (
                            <span className="mb-1 block text-[0.7em] opacity-70">
                              {item.count ?? 1}× {item.name}
                            </span>
                          )}
                          {rows.map((row) => (
                            <span key={row.id} className="flex items-start gap-2 py-0.5">
                              <span className="w-[2em] shrink-0 text-[1.1em] font-bold tabular-nums">{row.count}×</span>
                              <span className="min-w-0 flex-1">
                                <span className="block break-words">{row.name}</span>
                                {row.modifications.map((modification, index) => (
                                  <span
                                    key={index}
                                    className={cn(
                                      'block text-[0.8em] font-semibold',
                                      modification.tone && 'my-0.5 w-fit rounded px-1.5 py-0.5 text-modifier-foreground',
                                      modification.tone === 'extra' && 'bg-modifier-extra',
                                      modification.tone === 'none' && 'bg-modifier-none',
                                    )}
                                  >
                                    {modification.text}
                                  </span>
                                ))}
                              </span>
                            </span>
                          ))}
                          {item.message && <span className="block text-[0.8em] font-semibold">{item.message}</span>}
                        </span>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </div>

      {(showsComplete || showsPrint || kept) && (
        <footer className="flex gap-2 border-t p-2">
          {showsPrint && (
            <Button size="sm" variant="outline" disabled={busy} onClick={onPrint} aria-label="Skriv ut bong">
              <Printer className="h-4 w-4" />
            </Button>
          )}
          {kept ? (
            <Button size="sm" className="flex-1" disabled={busy || remaining > 0} onClick={onClearKept}>
              <Check className="h-4 w-4" />
              Klar · ta bort kopian
            </Button>
          ) : (
            showsComplete &&
            (state === 'ready' ? (
              <Button size="sm" className="flex-1" disabled={busy || displayId !== 'main'} onClick={onPickedUp}>
                <Check className="h-4 w-4" />
                Hämtad
              </Button>
            ) : (
              <Button size="sm" className="flex-1" disabled={busy || Boolean(slip.needsConfirmation) || remaining > 0} onClick={onFinishSlip}>
                <Check className="h-4 w-4" />
                {remaining > 0 ? `${remaining} artiklar kvar` : finishLabel}
              </Button>
            ))
          )}
        </footer>
      )}
    </article>
  )
}
